import { Folder, LockSimple, Sparkle } from "@phosphor-icons/react";

export const DYNAMIC_LABELS = {
  contractor_split: "One folder per contractor",
  subproject_split: "One folder per sub-project",
  month_split: "One folder per month",
  month_subproject_split: "Months with sub-project folders",
};

export const CONTENT_LABELS = {
  drawing: "Drawings",
  document: "Documents",
  correspondence: "Correspondence",
  invoice: "Invoices",
  contract: "Contracts",
  report: "Reports",
  photo: "Photos",
  calculation: "Calculations",
};

export function LayoutBadges({ schema, compact = false }) {
  const badges = [];
  if (schema?.has_contractor_split) badges.push("Contractor-based");
  if (schema?.has_month_split) badges.push("Monthly");
  if (schema?.has_subproject_split) badges.push("Sub-projects");
  if (!badges.length) badges.push("Fixed folders");
  return (
    <div className="flex flex-wrap gap-1.5">
      {badges.map((badge) => (
        <span key={badge} className={`${compact ? "text-[9px] px-1.5 py-0.5" : "text-[10px] px-2 py-0.5"} rounded-full border border-blue-100 bg-blue-50 font-medium text-blue-700`}>
          {badge}
        </span>
      ))}
    </div>
  );
}

const MAX_PREVIEW_DEPTH = 12;

function flattenFolderNodes(folders) {
  const rows = [];
  const stack = [...folders].reverse().map((node, index) => ({
    node,
    depth: 0,
    path: String(folders.length - index - 1),
    ancestors: [],
  }));

  while (stack.length > 0) {
    const current = stack.pop();
    const { node, depth, path, ancestors } = current;
    if (!node || typeof node !== "object") continue;
    const identity = node.key || `${node.name_template || node.dynamic_naming || "folder"}:${path}`;
    const circular = ancestors.includes(identity);
    if (circular || depth > MAX_PREVIEW_DEPTH) {
      rows.push({ key: `guard-${path}`, depth, circular, truncated: !circular });
      continue;
    }

    rows.push({ key: `${identity}-${path}`, node, depth });
    const children = Array.isArray(node.children)
      ? node.children.filter((child) => child && typeof child === "object")
      : [];
    const nextAncestors = [...ancestors, identity];
    for (let index = children.length - 1; index >= 0; index -= 1) {
      stack.push({
        node: children[index],
        depth: depth + 1,
        path: `${path}.${index}`,
        ancestors: nextAncestors,
      });
    }
  }
  return rows;
}

function FolderPreviewRow({ row }) {
  const safeDepth = Math.min(row.depth, MAX_PREVIEW_DEPTH);
  if (!row.node) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[10px] text-amber-700" style={{ marginLeft: safeDepth * 18 }}>
        {row.circular ? "Circular folder reference hidden" : "Additional nesting hidden from preview"}
      </div>
    );
  }
  const { node } = row;
  const accepted = (node.accepted_types || []).map((type) => CONTENT_LABELS[type] || type);
  return (
    <div className="relative flex items-start gap-2.5 rounded-md border border-slate-200 bg-white px-3 py-2.5" style={{ marginLeft: safeDepth * 18 }}>
      {safeDepth > 0 && <span className="absolute -left-[19px] top-1/2 w-[18px] border-t border-slate-200" />}
      <Folder size={15} weight="fill" className={node.dynamic_type ? "text-violet-500 mt-0.5" : "text-amber-400 mt-0.5"} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-semibold text-slate-800">{node.name_template || node.dynamic_naming || "Dynamic folder"}</span>
          {node.dynamic_type && <span className="inline-flex items-center gap-1 rounded bg-violet-50 px-1.5 py-0.5 text-[9px] font-semibold text-violet-700"><Sparkle size={9} weight="fill" />{DYNAMIC_LABELS[node.dynamic_type]}</span>}
          {node.required && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-medium text-slate-600">Required</span>}
          {node.protection && node.protection !== "none" && <span title={`${node.protection} folder`}><LockSimple size={11} className="text-slate-400" /></span>}
        </div>
        <p className="mt-0.5 text-[11px] leading-relaxed text-slate-500">{node.purpose || "No folder guidance has been added yet."}</p>
        {accepted.length > 0 && <p className="mt-1 text-[10px] text-slate-400">Put here: {accepted.join(", ")}</p>}
      </div>
    </div>
  );
}

export function FolderTreePreview({ schema, emptyText = "No folders in this layout." }) {
  const folders = Array.isArray(schema?.folders) ? schema.folders : [];
  if (!folders.length) return <p className="rounded-md border border-dashed border-slate-200 px-4 py-8 text-center text-xs text-slate-400">{emptyText}</p>;
  const rows = flattenFolderNodes(folders);
  return <div className="space-y-1.5" data-testid="folder-layout-tree-preview">{rows.map((row) => <FolderPreviewRow key={row.key} row={row} />)}</div>;
}
